import { Time } from '@internationalized/date';

/**
 * Detects the component type and returns appropriate props adapter.
 *
 * This function detects which atom component is being used
 * and returns the correct prop mapping for React Hook Form.
 *
 * Supported components:
 * - TextInput: `value`, `onChange`, `onBlur`, `hasError` (string), `label` (suppressed)
 * - OldTextInput: `value`, `onChange`, `onBlur`, `hasError` (boolean), `isTouched`
 * - TextArea: `value`, `onChange`, `onBlur`, `hasError`
 * - Checkbox: `isSelected`, `onChange`, `onBlur`, `hasError`
 * - Switch: `isSelected`, `onChange`, `onBlur`, `hasError`
 * - RadioGroup: `value`, `onChange`, `onBlur`, `hasError`, `isTouched`
 * - Dropdown: `value`, `onSelectionChange`, `hasError`
 * - DateInput: `value`, `onChange`, `onBlur`, `hasError`
 * - TimeInput: `value`, `onChange`, `onBlur`, `hasError`, `isTouched`
 * - AddressInput: `value`, `onChange`, `onBlur`, `invalidData`, `label` (suppressed)
 * - ChipPickerFormField: handled directly via useController (no adapter needed)
 *
 * For unknown components, falls back to generic `value`, `onChange`, `onBlur` pattern.
 *
 * @param child - The React element (atom component) to adapt
 * @param field - React Hook Form controller field props
 * @param fieldState - React Hook Form field state (errors, touched, etc.)
 * @returns Object with props mapped to the component's expected interface
 */
function getFieldAdapter(child, field, fieldState) {
    const componentName = getComponentName(child);
    const errorMessage = fieldState.error?.message;
    // Base props that work for most components
    const baseProps = {
        name: field.name,
        hasError: errorMessage || false,
        label: undefined, // Suppress built-in label
    };
    // Component-specific adapters
    switch (componentName) {
        case 'TextInput':
            return {
                ...baseProps,
                value: field.value ?? '',
                onChange: (value) => field.onChange(value),
                onBlur: field.onBlur,
            };
        case 'OldTextInput':
            return {
                ...baseProps,
                value: field.value ?? '',
                onChange: (value) => field.onChange(value),
                onBlur: field.onBlur,
                isTouched: fieldState.isTouched,
            };
        case 'TextArea':
            return {
                ...baseProps,
                value: field.value ?? '',
                onChange: (value) => field.onChange(value),
                onBlur: field.onBlur,
            };
        case 'Checkbox':
            return {
                ...baseProps,
                isSelected: field.value ?? false,
                onChange: (isSelected) => field.onChange(isSelected),
                onBlur: field.onBlur,
            };
        case 'Switch':
            return {
                ...baseProps,
                isSelected: field.value ?? false,
                onChange: (isSelected) => field.onChange(isSelected),
                onBlur: field.onBlur,
            };
        case 'RadioGroup':
            return {
                ...baseProps,
                value: field.value ?? '',
                onChange: (value) => field.onChange(value),
                onBlur: field.onBlur,
                isTouched: fieldState.isTouched,
            };
        case 'Dropdown':
            return {
                ...baseProps,
                value: field.value ?? null,
                onSelectionChange: (value) => field.onChange(value),
                isTouched: fieldState.isTouched,
            };
        case 'DateInput':
            return {
                ...baseProps,
                value: field.value ?? null,
                onChange: (value) => field.onChange(value),
                onBlur: field.onBlur,
                isTouched: fieldState.isTouched,
            };
        case 'TimeInput': {
            // Convert string values to TimeValue objects for React Aria
            const timeValue = (() => {
                if (!field.value)
                    return null;
                // If it's already a TimeValue object, use it as-is
                if (typeof field.value === 'object' &&
                    'hour' in field.value &&
                    'minute' in field.value &&
                    typeof field.value.hour === 'number' &&
                    typeof field.value.minute === 'number') {
                    return field.value;
                }
                // If it's a string, parse it to TimeValue
                if (typeof field.value === 'string') {
                    const parts = field.value.split(':');
                    if (parts.length >= 2) {
                        const hour = parseInt(parts[0], 10);
                        const minute = parseInt(parts[1], 10);
                        if (!isNaN(hour) && !isNaN(minute)) {
                            return new Time(hour, minute, 0);
                        }
                    }
                }
                return null;
            })();
            // Handle onChange - convert TimeValue back to string if original was string
            const handleTimeChange = (value) => {
                if (!value) {
                    field.onChange(null);
                    return;
                }
                // Check if the original value was a string to maintain format consistency
                if (typeof field.value === 'string' ||
                    field.value === null ||
                    field.value === undefined) {
                    // Convert to string format (HH:MM)
                    const hour = value.hour.toString().padStart(2, '0');
                    const minute = value.minute.toString().padStart(2, '0');
                    field.onChange(`${hour}:${minute}`);
                }
                else {
                    // Keep as TimeValue object if that's what was originally stored
                    field.onChange(value);
                }
            };
            return {
                ...baseProps,
                value: timeValue,
                onChange: handleTimeChange,
                onBlur: field.onBlur,
                isTouched: fieldState.isTouched,
            };
        }
        case 'AddressInput':
            // AddressInput expects value as Address | null and onChange with (address, edited) signature
            // It also supports invalidData for nested field errors
            return {
                ...baseProps,
                value: field.value ?? null,
                onChange: (address) => {
                    // AddressInput's onChange signature is (address, edited)
                    // We only pass the address to RHF
                    field.onChange(address);
                },
                onBlur: field.onBlur,
                invalidData: getNestedAddressErrors(fieldState.error),
            };
        default:
            // Generic adapter for unknown components
            // Try common prop patterns
            return {
                ...baseProps,
                value: field.value,
                onChange: field.onChange,
                onBlur: field.onBlur,
            };
    }
}
/**
 * Gets the component name from a React element.
 *
 * Extracts the display name or function name from various React element types:
 * - String types (native HTML elements): returns the tag name
 * - Function components: returns displayName or function name
 * - forwardRef components: returns displayName or 'ForwardRef'
 *
 * @param element - React element to inspect
 * @returns Component name as string, or 'Unknown' if unable to determine
 */
function getComponentName(element) {
    if (!element?.type) {
        return 'Unknown';
    }
    // Handle string types (native HTML elements)
    if (typeof element.type === 'string') {
        return element.type;
    }
    // Handle function components
    if (typeof element.type === 'function') {
        return element.type.displayName || element.type.name || 'Unknown';
    }
    // Handle forwardRef components
    if (element.type && typeof element.type === 'object' && 'render' in element.type) {
        return element.type.displayName || 'ForwardRef';
    }
    return 'Unknown';
}
/**
 * Extracts nested validation errors from React Hook Form field error for AddressInput.
 *
 * AddressInput expects invalidData in the format:
 * `Partial<Record<keyof Address, string | boolean>>`
 *
 * Zod nested errors can appear in different structures:
 * - error.types with nested field errors
 * - error.message for top-level errors
 * - Nested FieldError objects
 *
 * @param error - React Hook Form field error
 * @returns Object with nested field errors or undefined
 */
function getNestedAddressErrors(error) {
    if (!error || !('types' in error))
        return undefined;
    const invalidData = {};
    // Check if error has nested types (Zod object validation)
    if (error.types && typeof error.types === 'object') {
        // Iterate through nested field errors
        Object.keys(error.types).forEach((key) => {
            const fieldError = error.types[key];
            if (fieldError) {
                // FieldError can be a string message or an object with message
                const message = typeof fieldError === 'string'
                    ? fieldError
                    : fieldError?.message || fieldError?.type || true;
                invalidData[key] = message;
            }
        });
    }
    // If we found nested errors, return them
    if (Object.keys(invalidData).length > 0) {
        return invalidData;
    }
    // If there's a top-level error message but no nested structure,
    // we could set a general error, but AddressInput works better with field-specific errors
    // So we return undefined and let the component handle the general error via hasError
    return undefined;
}

export { getFieldAdapter };
//# sourceMappingURL=fieldAdapters.js.map
